import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { codingService, CodingProblem } from '../api/services/coding';
import Container from '../components/common/Container';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CodeEditor from '../components/Coding/CodeEditor';

const CodingProblemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [problem, setProblem] = useState<CodingProblem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<'python' | 'java' | 'cpp' | 'c' | 'javascript'>('python');
  const [executionResult, setExecutionResult] = useState<any>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previousSolutions, setPreviousSolutions] = useState<any[]>([]);
  const [isLoadingSolutions, setIsLoadingSolutions] = useState(false);
  const [hasLoadedSolutions, setHasLoadedSolutions] = useState(false);
  const [hasLoadedActualSolution, setHasLoadedActualSolution] = useState(false);
  const [isMounted, setIsMounted] = useState(true);

  const getStarterCode = useCallback(() => {
    if (!problem) return '';
    return problem.starter_code?.[selectedLanguage] || `# Write your solution here
# Problem: ${problem.title}

def solve():
    # Your code here
    pass

# Test your solution
if __name__ == "__main__":
    result = solve()
    print(result)`;
  }, [problem, selectedLanguage]);

  // Fetch problem details
  useEffect(() => {
    const fetchProblem = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        setError(null);
        const response = await codingService.getProblem(parseInt(id));
        setProblem(response.data);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Failed to load problem');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProblem();
  }, [id]);

  // Load previous solutions
  useEffect(() => {
    if (!problem || hasLoadedSolutions) return;

    const loadSolutions = async () => {
      try {
        setIsLoadingSolutions(true);
        const response = await codingService.getMySolutions(problem.id);
        const solutions = response.data;

        if (solutions.length > 0) {
          // Find the most recent solution for the current language
          const recentSolution = solutions
            .filter((s: any) => s.language === selectedLanguage)
            .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

          if (recentSolution) {
            setCode(recentSolution.solution_code);
            setHasLoadedActualSolution(true);
          } else {
            setCode(getStarterCode());
          }
        } else {
          setCode(getStarterCode());
        }
      } catch (err) {
        console.error('Failed to load previous solutions:', err);
        setCode(getStarterCode());
      } finally {
        setIsLoadingSolutions(false);
        setHasLoadedSolutions(true);
      }
    };

    // Add a small delay to stagger API calls
    const timer = setTimeout(loadSolutions, Math.random() * 1000);
    return () => clearTimeout(timer);
  }, [problem, selectedLanguage, hasLoadedSolutions, getStarterCode]);

  // Reset to starter code when language changes (only if no actual solution was loaded)
  useEffect(() => {
    if (hasLoadedSolutions && !isLoadingSolutions && !hasLoadedActualSolution) {
      setCode(getStarterCode());
    }
  }, [selectedLanguage, hasLoadedSolutions, isLoadingSolutions, hasLoadedActualSolution, getStarterCode]);

  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  const handleExecuteCode = async () => {
    if (!problem || !code.trim()) return;

    try {
      setIsExecuting(true);
      setExecutionResult(null);
      const response = await codingService.executeCode(problem.id, code, selectedLanguage);
      if (isMounted) {
        setExecutionResult(response.data);
      }
    } catch (err: any) {
      if (isMounted) {
        setExecutionResult({
          success: false,
          error: err?.response?.data?.error || 'Failed to execute code'
        });
      }
    } finally {
      if (isMounted) {
        setIsExecuting(false);
      }
    }
  };

  const handleSubmitSolution = async () => {
    if (!problem || !code.trim()) return;

    try {
      setIsSubmitting(true);
      await codingService.submitSolution(problem.id, {
        code,
        language: selectedLanguage
      });

      // Reload solutions to show the new submission
      setHasLoadedSolutions(false);
      setHasLoadedActualSolution(false);

      // Show success message
      alert('Solution submitted successfully!');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to submit solution');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToProblems = () => {
    // Get current tenant from URL or use default
    const currentPath = window.location.pathname;
    const tenantMatch = currentPath.match(/^\/([^\/]+)/);
    const tenant = tenantMatch ? tenantMatch[1] : '';

    if (tenant && tenant !== 'coding') {
      navigate(`/${tenant}/coding`);
    } else {
      navigate('/coding');
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'very_easy':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'easy':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'difficult':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'very_difficult':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    return difficulty.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getCategoryLabel = (category: string) => {
    return category.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (isLoading) {
    return (
      <Container className="px-4 py-6">
        <LoadingSpinner />
      </Container>
    );
  }

  if (error || !problem) {
    return (
      <Container className="px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-6 text-center">
            <div className="text-red-600 dark:text-red-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-red-900 dark:text-red-100 mb-2">Error</h3>
            <p className="text-red-800 dark:text-red-200">{error || 'Problem not found'}</p>
            <button
              onClick={handleBackToProblems}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Problems
            </button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="px-4 py-6 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBackToProblems}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Problems
          </button>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{problem.title}</h1>
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getDifficultyColor(problem.difficulty)}`}>
                  {getDifficultyLabel(problem.difficulty)}
                </span>
                {problem.category && (
                  <span className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                    {getCategoryLabel(problem.category)}
                  </span>
                )}
                {problem.company_tags && problem.company_tags.length > 0 && (
                  <span className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full">
                    {problem.company_tags.join(', ')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Problem Description */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Problem Statement</h2>
              <div className="prose dark:prose-invert max-w-none">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{problem.problem_statement}</p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Input Format</h2>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{problem.input_format}</pre>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Output Format</h2>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{problem.output_format}</pre>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Sample Input/Output</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sample Input</h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                    <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{problem.sample_input}</pre>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sample Output</h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                    <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{problem.sample_output}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Code Editor and Execution */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Code Editor</h2>
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value as 'python' | 'java' | 'cpp' | 'c' | 'javascript')}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="python">Python</option>
                  <option value="javascript">JavaScript</option>
                  <option value="java">Java</option>
                  <option value="cpp">C++</option>
                  <option value="c">C</option>
                </select>
              </div>

              <div className="mb-4">
                <CodeEditor
                  value={code}
                  onChange={setCode}
                  language={selectedLanguage as 'python' | 'java' | 'cpp' | 'c' | 'javascript'}
                  key={`problem-${problem.id}`}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleExecuteCode}
                  disabled={isExecuting || !code.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isExecuting ? 'Running...' : 'Run Code'}
                </button>
                <button
                  onClick={handleSubmitSolution}
                  disabled={isSubmitting || !code.trim()}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Solution'}
                </button>
              </div>
            </div>

            {/* Execution Results */}
            {executionResult && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Execution Results</h2>

                {executionResult.success ? (
                  <div className="space-y-4">
                    {executionResult.results && Array.isArray(executionResult.results) && executionResult.results.map((result: any, index: number) => (
                      <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Test Case {index + 1}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            result.passed ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}>
                            {result.passed ? 'Passed' : 'Failed'}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Input:</span>
                            <div className="bg-gray-50 dark:bg-gray-700 rounded p-2 mt-1">
                              <pre className="whitespace-pre-wrap">{result.input}</pre>
                            </div>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700 dark:text-gray-300">Expected:</span>
                            <div className="bg-gray-50 dark:bg-gray-700 rounded p-2 mt-1">
                              <pre className="whitespace-pre-wrap">{result.expected_output}</pre>
                            </div>
                          </div>
                        </div>

                        <div className="mt-3">
                          <span className="font-medium text-gray-700 dark:text-gray-300">Output:</span>
                          <div className="bg-gray-50 dark:bg-gray-700 rounded p-2 mt-1">
                            <pre className="whitespace-pre-wrap">{result.actual_output}</pre>
                          </div>
                        </div>

                        {result.error && (
                          <div className="mt-3">
                            <span className="font-medium text-red-700 dark:text-red-400">Error:</span>
                            <div className="bg-red-50 dark:bg-red-900/20 rounded p-2 mt-1">
                              <pre className="text-red-700 dark:text-red-400 whitespace-pre-wrap">{result.error}</pre>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                    <div className="text-red-800 dark:text-red-200">
                      {executionResult.error || 'Execution failed'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default CodingProblemDetail;
